/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One reading of a duration, for every place a value becomes `durationSeconds`** (`#933`, `#1320`).
 *
 * `durationSeconds` is what the optimizer fits an ad into a pod with (`pod-optimization-base.ts`), so
 * a string there is not a cosmetic defect: `curDuration + "00:00:30"` concatenates, and a comparison
 * against the pod's limits reads `NaN` and lets the ad through. There were three writers and they
 * disagreed:
 *
 * | Writer | Before |
 * | --- | --- |
 * | the VAST driver's legacy pixel extraction | converted, through a private copy |
 * | the base driver's pixel extraction (the ORTB path) | **assigned the raw value** |
 * | a response mapping onto `ad.durationSeconds` | cast by `asType`: `"00:00:30"` became `NaN` and was skipped under `number`, and was **written as a string** under `string` or no type |
 *
 * And `<Duration>` itself was parsed by a private `hhmmssToSeconds` in each of the two drivers.
 */

/**
 * `hh:mm:ss`, `mm:ss` or a plain number of seconds, as written in a VAST `<Duration>`.
 *
 * `undefined` for an empty value. Deliberately lenient about what it parses — the drivers use it on
 * the protocol's own field and fall back to 30s — so a caller deciding whether a value may **replace**
 * one already parsed uses {@link toDurationSeconds}, which rejects anything that is not usable.
 */
export function hhmmssToSeconds(duration: string | undefined): number | undefined {
  if (!duration) return undefined;
  const parts = duration.split(':').map((p) => parseInt(p, 10));
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  const n = parseInt(duration, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * **Usable seconds, or `undefined`** — the rule for a value that would overwrite a parsed duration.
 *
 * Accepts a positive finite number, or a string that is **wholly** a duration: `hh:mm:ss`, `mm:ss` or
 * plain seconds, with a fraction allowed on the seconds (VAST permits `00:00:30.500`). Rejects `NaN`,
 * zero, negatives and every other shape, so the caller keeps the duration it already has rather than
 * writing one no pod can be built from.
 *
 * **Strict where {@link hhmmssToSeconds} is lenient, and that is the point.** `parseInt` reads a
 * numeric *prefix*, so through the lenient reader `"12abc"` is 12 seconds and `"1.50 USD"` is 1 —
 * the trap `coerceValue`'s `parseFloat` fell into, and which `response-mapping.resolver.spec.ts`
 * already guards against on this very target. A value that is not wholly a duration is not one.
 */
export function toDurationSeconds(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value !== 'string') return undefined;
  const parts = value.trim().split(':');
  if (parts.length > 3) return undefined;
  const whole = /^\d+$/;
  const last = /^\d+(\.\d+)?$/;
  if (!parts.every((p, i) => (i === parts.length - 1 ? last : whole).test(p))) return undefined;
  const seconds = parts.reduce((total, p) => total * 60 + Number(p), 0);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}
